// The life of a lab report version (docs/plans/phase1-lab.md §2.4, §5.4, §7).
//
//   in_progress ──submit──▶ awaiting_validation ──validate──▶ final                 (one stage)
//                                               ──validate──▶ technically_validated
//                                                             ──authorise──▶ final (two stages)
//   awaiting_validation | technically_validated ──return──▶ in_progress
//   final ──supersede──▶ superseded (when a corrected version is released)
//   anything live ──mark_in_error──▶ entered_in_error
//
// Only `final` is visible outside the lab. Nothing leaves `superseded` or `entered_in_error`.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const LAB_REPORT_STATUSES = [
  'in_progress',
  'awaiting_validation',
  'technically_validated',
  'final',
  'superseded',
  'entered_in_error',
] as const;
export type LabReportStatus = (typeof LAB_REPORT_STATUSES)[number];

export const LAB_REPORT_ACTIONS = [
  'save',
  'submit',
  'validate',
  'authorise',
  'return',
  'supersede',
  'mark_in_error',
] as const;
export type LabReportAction = (typeof LAB_REPORT_ACTIONS)[number];

export type LabValidationStages = 'one' | 'two';

export type LabReportTransitionErrorCode = 'lab_report.transition_not_allowed';

export function labReportTransition(
  from: LabReportStatus,
  action: LabReportAction,
  stages: LabValidationStages,
): Result<LabReportStatus, LabReportTransitionErrorCode> {
  const refuse = err('lab_report.transition_not_allowed' as const);
  switch (action) {
    case 'save':
      return from === 'in_progress' ? ok('in_progress') : refuse;
    case 'submit':
      return from === 'in_progress' ? ok('awaiting_validation') : refuse;
    case 'validate':
      if (from !== 'awaiting_validation') return refuse;
      return ok(stages === 'one' ? 'final' : 'technically_validated');
    case 'authorise':
      return from === 'technically_validated' && stages === 'two' ? ok('final') : refuse;
    case 'return':
      return from === 'awaiting_validation' || from === 'technically_validated'
        ? ok('in_progress')
        : refuse;
    case 'supersede':
      return from === 'final' ? ok('superseded') : refuse;
    case 'mark_in_error':
      return from === 'superseded' || from === 'entered_in_error' ? refuse : ok('entered_in_error');
  }
}

/** Whether a report in this status is released to the patient record. */
export function isReleasedLabReport(status: LabReportStatus): boolean {
  return status === 'final';
}

/**
 * Whether a report in this status carries results the ordering clinician is told about
 * when critical (C4): released, or validated by the lab in a two-stage lab.
 */
export function isLabValidated(status: LabReportStatus): boolean {
  return status === 'final' || status === 'technically_validated';
}

export type LabValidatorErrorCode =
  'lab_report.validator_must_differ' | 'lab_report.authoriser_must_differ';

/**
 * The people rule for a validation step (C8). Technical validation (or the only stage) is
 * refused to anyone who entered a current result on the report when the facility requires
 * a different person. Clinical authorisation is always a second person: never the one who
 * validated, and, under the same setting, never one who entered.
 */
export function checkValidator(input: {
  readonly action: 'validate' | 'authorise';
  readonly actorId: string;
  readonly enteredBy: readonly string[];
  readonly technicallyValidatedBy: string | null;
  readonly validatorMustDiffer: boolean;
}): Result<true, LabValidatorErrorCode> {
  const entered = input.enteredBy.includes(input.actorId);
  if (input.action === 'validate') {
    return input.validatorMustDiffer && entered
      ? err('lab_report.validator_must_differ')
      : ok(true);
  }
  if (input.technicallyValidatedBy === input.actorId)
    return err('lab_report.authoriser_must_differ');
  if (input.validatorMustDiffer && entered) return err('lab_report.authoriser_must_differ');
  return ok(true);
}
